import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { prop, path, isEmpty } from 'ramda';
import { SimilarExperiences, Page } from './experienceHome.styles';
import {
  hasAnyPropChanged,
  renderWhenNotNil,
  renderWhenTrue,
  renderWhenNotNilOrEmpty,
} from '../../../shared/utils/rendering';
import messages from './experienceHome.messages';
import { VendorSection } from '../vendorSection';
import { EntityRefData } from '../../../shared/components/entityRefData';
import { ExperienceInfo } from '../experienceInfo';
import { LocationSection } from '../locationSection/locationSection.component';
import { ExperienceTestimonials } from '../experienceTestimonials';
import { Divider } from '../../../shared/components/divider';
import { meaningfulProps } from './experienceHome.constants';
import { Helmet } from '../../../shared/components/helmet';
import { GTMEvents, GTMList } from '../../../modules/analytics/analytics.constants';

export const DISCOUNT_SECTION_OFFSET = 185;

export class ExperienceHome extends Component {
  static propTypes = {
    experience: PropTypes.object,
    similarExperiences: PropTypes.arrayOf(PropTypes.object),
    intl: PropTypes.object,
    kioskHomeUrl: PropTypes.string,
    sameMarketExperiencesLength: PropTypes.number,
    showPolicy: PropTypes.func,
    scrollTo: PropTypes.func,
    currentExperienceParentRoute: PropTypes.string,
    contextName: PropTypes.string,
    logGTMEvent: PropTypes.func.isRequired,
  };

  static defaultProps = {
    experience: {},
  };

  shouldComponentUpdate(nextProps) {
    return hasAnyPropChanged(meaningfulProps, this.props, nextProps);
  }

  get socialImageAsset() {
    return path(['heroGallery', 0])(this.props.experience);
  }

  priceBreakdownRef = React.createRef();

  handleDiscountInfoClick = () => {
    if (!this.priceBreakdownRef.current) {
      return;
    }

    const { top } = this.priceBreakdownRef.current.getBoundingClientRect();

    this.props.scrollTo({
      y: top + window.pageYOffset - DISCOUNT_SECTION_OFFSET,
      smooth: true,
    });
  };

  handleViewportEnter = experiences =>
    this.props.logGTMEvent(GTMEvents.ViewedExperiences, { experiences, list: GTMList.Related });

  renderVendorSection = vendorData =>
    renderWhenTrue(() => <VendorSection data={vendorData} />)(
      vendorData && !!vendorData.firstName && !!vendorData.intro
    );

  render() {
    const {
      intl,
      similarExperiences,
      experience,
      kioskHomeUrl,
      sameMarketExperiencesLength,
      contextName,
      showPolicy,
    } = this.props;

    return (
      <Page key={experience.id} isLoading={isEmpty(experience)}>
        <Helmet title={experience.name} description={experience.description} imageAssetId={this.socialImageAsset} />

        <ExperienceInfo
          experience={experience}
          kioskHomeUrl={kioskHomeUrl}
          onDiscountInfoClick={this.handleDiscountInfoClick}
        />

        {renderWhenNotNilOrEmpty(() => (
          <>
            <EntityRefData entityType="vendors" id={prop('vendor', experience)}>
              {this.renderVendorSection}
            </EntityRefData>

            {renderWhenNotNil(testimonials => <ExperienceTestimonials testimonials={testimonials} />)(
              prop('testimonials', experience)
            )}

            <LocationSection
              priceBreakdownRef={this.priceBreakdownRef}
              experience={experience}
              showPolicy={showPolicy}
            />

            {renderWhenTrue(() => (
              <Fragment>
                <Divider horizontalPadding />

                <SimilarExperiences
                  visibilitySensorActive
                  onViewportEnter={this.handleViewportEnter}
                  title={intl.formatMessage(messages.similarExperiences)}
                  linkUrl={this.props.currentExperienceParentRoute}
                  linkLabel={this.props.intl.formatMessage(messages.allSimilarExperiences, {
                    count: sameMarketExperiencesLength,
                    market: contextName,
                  })}
                  experiences={similarExperiences}
                  limitedCount
                />
              </Fragment>
            ))(similarExperiences.length > 0)}
          </>
        ))(experience)}
      </Page>
    );
  }
}
